import { Injectable, OnDestroy } from '@angular/core';
import { AnalyticsRangeContext, DynamicQueryService } from '@services/analytics-services/core';
import { AggregatedStatDataService } from '@services/analytics-services/core/analytics-aggregated-stat.service';
import { ChartType, QueryType } from '@services/analytics-services/core/models/matrix-stats';
import { OrderAnalyticsService } from '@services/analytics-services/orders';
import { OrderProductStats } from '@services/analytics-services/orders/models/order-product';
import { ProductPerformanceStats } from '@services/analytics-services/products/models/product-performance-stats';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { DateRangePresetType, ProcessingState } from '@services/core-services/models';
import { BehaviorSubject, Observable, Subject, catchError, of, tap } from 'rxjs';
import { DashboardStore } from '../dashboard/dashboard.store';
import { TopProductList } from '@services/analytics-services/products/constants/top-product-table-header';
import { productPerformanceList } from '@services/analytics-services/products/constants/product-performance-table-header';
import {
  ClientSideSortingService,
  SortConfig,
} from '@services/core-services/client-side-sorting.service';
import { AutoTableSortingHelper } from '@services/core-services/auto-table-sorting.mixin';

@Injectable()
export class DashboardProductsStore implements OnDestroy {
  // State
  private products = new BehaviorSubject<OrderProductStats[] | undefined>(undefined);
  private originalProducts = new BehaviorSubject<OrderProductStats[] | undefined>(undefined);
  private productPerformance = new BehaviorSubject<ProductPerformanceStats[] | undefined>(
    undefined
  );
  private originalProductPerformance = new BehaviorSubject<ProductPerformanceStats[] | undefined>(
    undefined
  );
  private topProductsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private productPerformanceLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private errorMessage = new BehaviorSubject<string>('');
  private productPerformanceErrorMessage = new BehaviorSubject<string>('');
  private destroy$ = new Subject<void>();

  // Client-side sorting handlers
  private topProductsSortHandler: ((sortConfig: SortConfig) => void) | undefined;
  private productPerformanceSortHandler: ((sortConfig: SortConfig) => void) | undefined;
  private currentSortConfig: SortConfig = {};

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly aggregatedStatDataService: AggregatedStatDataService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly orderAnalyticsService: OrderAnalyticsService,
    private readonly clientSideSortingService: ClientSideSortingService
  ) {
    // Initialize client-side sorting handler for top products
    this.topProductsSortHandler = AutoTableSortingHelper.createSortHandler(
      this.clientSideSortingService,
      this.products,
      this.originalProducts,
      TopProductList,
      {
        // Only add custom extractors if needed
        product_name: (item: OrderProductStats) => item.product_name?.toLowerCase() || '',
        total_value: (item: OrderProductStats) =>
          (item?.quantity || 0) * (item?.average_selling_price || 0),
      }
    );

    this.productPerformanceSortHandler = AutoTableSortingHelper.createSortHandler(
      this.clientSideSortingService,
      this.productPerformance,
      this.originalProductPerformance,
      productPerformanceList,
      {}
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTopProducts(): Observable<OrderProductStats[] | undefined> {
    return this.products.asObservable();
  }

  getProductperformance(): Observable<ProductPerformanceStats[] | undefined> {
    return this.productPerformance.asObservable();
  }

  getTopProductsLoader(): Observable<ProcessingState> {
    return this.topProductsLoader.asObservable();
  }

  getProductPerformanceLoader(): Observable<ProcessingState> {
    return this.productPerformanceLoader.asObservable();
  }

  getErrorMessage(): Observable<string> {
    return this.errorMessage.asObservable();
  }

  getProductPerformanceErrorMessage(): Observable<string> {
    return this.productPerformanceErrorMessage.asObservable();
  }

  /**
   * Sort top products client-side without making a new API call
   */
  sortTopProducts(sortConfig: SortConfig): void {
    this.currentSortConfig = sortConfig; // Store current sort state
    if (this.topProductsSortHandler) {
      this.topProductsSortHandler(sortConfig);
    }
  }

  sortTopPerformanceProducts(sortConfig: SortConfig): void {
    this.currentSortConfig = sortConfig;
    if (this.productPerformanceSortHandler) {
      this.productPerformanceSortHandler(sortConfig);
    }
  }

  /**
   * Check if there's an active sort configuration for top products
   */
  private hasActiveSort(): boolean {
    return Object.values(this.currentSortConfig).some(direction => direction !== 'default');
  }

  loadBestSellingProducts(context: AnalyticsRangeContext): Observable<OrderProductStats[]> {
    const { range, interval, preset } = context;
    return this.orderAnalyticsService
      .getTopSellingProducts({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
      })
      .pipe(
        tap((products: OrderProductStats[]) => {
          this.topProductsLoader.next(ProcessingState.Success);
          this.originalProducts.next([...products]); // Store original data
          this.products.next(products);
          // Update sorting service with new data
          // Reapply current sort to new data if there was a previous sort
          if (this.hasActiveSort() && this.topProductsSortHandler) {
            this.topProductsSortHandler(this.currentSortConfig);
          }
        }),
        catchError(error => {
          this.topProductsLoader.next(ProcessingState.Error);
          this.handleError('Error loading best selling products', error);
          this.products.next(undefined);
          this.originalProducts.next(undefined);
          return of([] as OrderProductStats[]);
        })
      );
  }

  loadMostViewedProducts(context: AnalyticsRangeContext): Observable<ProductPerformanceStats[]> {
    this.productPerformanceLoader.next(ProcessingState.Loading);
    const { range, interval, preset } = context;

    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.CE_ITEM_STATS,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap(products => {
          this.productPerformanceLoader.next(ProcessingState.Success);
          this.originalProductPerformance.next([...products]); // Store original data
          this.productPerformance.next(products);
          // Reapply current sort to new data if there was a previous sort
          if (this.hasActiveSort() && this.productPerformanceSortHandler) {
            this.productPerformanceSortHandler(this.currentSortConfig);
          }
        }),
        catchError(error => {
          this.productPerformanceLoader.next(ProcessingState.Error);
          this.handleError(
            'Error loading product performance',
            error,
            this.productPerformanceErrorMessage
          );
          this.productPerformance.next(undefined);
          this.originalProductPerformance.next(undefined);
          return of([] as ProductPerformanceStats[]);
        })
      );
  }

  private handleError(
    message: string,
    error: any,
    errorSubject: BehaviorSubject<string> = this.errorMessage
  ): void {
    const errorMessage = this.errorHandlerService.handleServerError(error);
    errorSubject.next(typeof errorMessage === 'string' ? errorMessage : message);
    console.error(message, error);
  }
}
